import * as React from "react";

import { Input, InputRef } from "antd";
import { TextAreaProps } from "antd/lib/input";
import { CellBox, FnFocus, PropsCellBox } from "./CellBox";

type InputValue = string | null | undefined;

type PropsCellBoxText<TValue extends InputValue, Res> = Omit<
  PropsCellBox<TValue, Res>,
  "children"
> & {
  textProps?: Omit<TextAreaProps, "onBlur" | "onChange" | "value">;
};

export const CellBoxText = <TValue extends InputValue, Res>(
  props: PropsCellBoxText<TValue, Res>,
) => {
  const { textProps, ...cellProps } = props;
  return (
    <CellBox<TValue, Res> {...cellProps} disableEnter>
      {(chProps) => <CellBoxTextChildren {...chProps} textProps={textProps} />}
    </CellBox>
  );
};

type PropsCellBoxTextChildren<TValue extends InputValue, Res> = {
  value: TValue | undefined;
  onChange: (v: TValue) => void;
  onBlur: () => void;
  textProps: PropsCellBoxText<TValue, Res>["textProps"];
  getFocus: (fn: FnFocus) => void;
};

const CellBoxTextChildren = <TValue extends InputValue, Res>(
  props: PropsCellBoxTextChildren<TValue, Res>,
) => {
  const { textProps, value, onChange, onBlur, getFocus } = props;
  const ref = React.useRef<InputRef>(null);
  React.useEffect(() => {
    getFocus(() => {
      ref.current?.focus();
    });
    return () => getFocus(undefined);
  }, [ref.current]);
  return (
    <Input.TextArea
      ref={ref}
      {...textProps}
      value={value as TextAreaProps["value"]}
      onChange={(e) => onChange(e.currentTarget.value as TValue)}
      onBlur={onBlur}
    />
  );
};
